import React, { useCallback, useEffect, useState } from 'react';

import { TFunction } from 'react-i18next';

import { InputStatus, ValidateFunction } from './InputStatus';
import { useValidationOnBlur } from './useValidationOnBlur';

interface TextFieldConfig {
  name: string;
  initialValue?: string;
  validate?: ValidateFunction<string>;
  t?: TFunction;
  lazyInitialValue?: boolean;
}

export interface TextFieldConnector {
  name: string;
  value?: string;
  reset: () => void;
  onChange?: (value: string) => void;
  inputStatus: InputStatus;
  message?: string;
  onFocus?: React.FocusEventHandler<HTMLInputElement | HTMLTextAreaElement>;
  onBlur?: React.FocusEventHandler<HTMLInputElement | HTMLTextAreaElement>;
  isValid: boolean;
}

export function useTextField({
  name,
  initialValue = '',
  validate,
  t,
  lazyInitialValue = false,
}: TextFieldConfig): TextFieldConnector {
  const [value, setValue] = useState<string | undefined>(initialValue);

  const {
    onBlur,
    onFocus,
    inputStatus,
    message,
    reset: resetValidation,
    isValid,
  } = useValidationOnBlur({
    value,
    validate,
    t,
  });

  const onChange = useCallback((value: string) => {
    setValue(value);
  }, []);

  useEffect(() => {
    if (!lazyInitialValue) return;
    if (!value && initialValue) setValue(initialValue);
  }, [lazyInitialValue, initialValue, value]);

  const reset = useCallback(() => {
    resetValidation();
    setValue(initialValue);
  }, [setValue, initialValue, resetValidation]);

  return {
    name,
    value,
    reset,
    inputStatus,
    isValid,
    message,
    onChange,
    onBlur,
    onFocus,
  };
}
